import { useCallback, useEffect, useMemo, useState } from 'react';
import s from './TagsList.module.scss';
import { Title } from '@tremor/react';
import { useLoaderData } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { CoreTable } from '@components/Tables/CoreTable/CoreTable';
import { differenceBy } from 'lodash';
import { TagsWithUserPermissions } from '@/types/types';
import { useWorkspaceIdFromParams } from '@/hooks/useWorkspaceIdFromParams';
import { TagsService } from '@/services/dataforseo/tagsService';
import { Checkbox } from '@/components/layouts/Inputs/Checkbox/Checkbox';
import { OutlinedButton } from '@/components/layouts/Buttons/OutlinedButton/OutlinedButton';
import { SearchBar } from '@/components/layouts/Inputs/SearchBar/SearchBar';
import { TableActions } from '@/components/Tables/KeywordsTable/TableActions';
import { DeleteIcon } from '@/assets';
import { AddTagsModal } from '@/components/layouts/Modals/AddTagsModal';
import { AddColorIcon } from '@/assets';

export type TableTag = {
  id: string;
  tagName: string;
};

const columnHelper = createColumnHelper<TableTag>();

export const TagsList = () => {
  const { tags } = useLoaderData() as TagsWithUserPermissions;
  const [tableTags, setTableTags] = useState<TableTag[]>(() =>
    tags.map((tag) => ({ id: tag.id, tagName: tag.name })),
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const workspaceId = useWorkspaceIdFromParams() as string;
  const [searchValue, setSearchValue] = useState('');
  const [rowSelection, setRowSelection] = useState({});

  const displayTags = useMemo(() => {
    if (searchValue === '') return tableTags;
    else {
      return tableTags.filter((tag) =>
        tag.tagName.toLowerCase().includes(searchValue.toLowerCase()),
      );
    }
  }, [searchValue, tableTags]);

  const handleDeleteTag = useCallback(
    async (rowId: number) => {
      const tagToDelete = tableTags[rowId];
      const afterDeletion = differenceBy(tableTags, [tagToDelete], 'id');
      setTableTags(afterDeletion);
      try {
        await TagsService.deleteTag(tagToDelete.id, workspaceId, 'dataforseo');
      } catch (e) {
        setTableTags((tags) => {
          const keywordsCopy = [...tags];
          keywordsCopy.splice(rowId, 0, tagToDelete);
          return keywordsCopy;
        });
      }
    },
    [tableTags, workspaceId],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', { cell: '', header: '' }),
      columnHelper.display({
        id: 'checkbox',
        cell: ({ row }) => <Checkbox row={row} />,
      }),
      columnHelper.accessor('tagName', {
        cell: (props) => (
          <section className={s.tagCell}>
            <span className={s.tagCellText}>{props.getValue()}</span>
          </section>
        ),
      }),
      columnHelper.display({
        header: 'Options',
        cell: (props) => (
          <OutlinedButton
            onClick={() => handleDeleteTag(parseInt(props.row.id))}
            icon={DeleteIcon}
            iconAltText="delete keywords icon"
          >
            Remove
          </OutlinedButton>
        ),
      }),
    ],
    [handleDeleteTag],
  );

  const table = useReactTable({
    data: displayTags,
    columns,
    state: {
      rowSelection,
    },
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
  });

  useEffect(() => {
    setTableTags(tags.map((tag) => ({ tagName: tag.name, id: tag.id })));
  }, [tags]);

  const onSearchBarSubmit = (value: string) => {
    setSearchValue(value);
  };

  const handleDeleteSelectedTags = async (selectedKeywords: Record<number, boolean>) => {
    const tableTagsIds = Object.entries(selectedKeywords)
      .filter(([, isSelected]) => isSelected)
      .map(([rowId]) => ({ id: tableTags[parseInt(rowId)].id }));
    const keywordsCopy = [...tableTags];
    const newKeywords = differenceBy(tableTags, tableTagsIds, 'id');
    setTableTags(newKeywords);

    try {
      await TagsService.deleteTags(tableTagsIds, workspaceId, 'dataforseo');
    } catch (e) {
      setTableTags(keywordsCopy);
    }
  };

  return (
    <>
      <section className={s.optionsWrapper}>
        <Title className={s.optionsTitle}>Tags Management</Title>
        <SearchBar onSubmit={onSearchBarSubmit} placeholder="Find Tag" />
      </section>
      <OutlinedButton
        style={{ marginTop: '21px' }}
        icon={AddColorIcon}
        iconAltText="add tag icon"
        onClick={() => setIsModalOpen(true)}
      >
        Add Tag
      </OutlinedButton>
      <TableActions
        table={table}
        onDeleteSelected={async () => {
          await handleDeleteSelectedTags(rowSelection);
          table.setRowSelection({});
        }}
      />
      <CoreTable table={table} />
      <TableActions
        table={table}
        onDeleteSelected={async () => {
          await handleDeleteSelectedTags(rowSelection);
          table.setRowSelection({});
        }}
      />
      <AddTagsModal isOpen={isModalOpen} setClose={() => setIsModalOpen(false)} />
    </>
  );
};
